// Sources: https://www.apollographql.com/docs/react/migrating/apollo-client-3-migration/
import {
  ApolloClient,
  HttpLink,
  InMemoryCache,
  split,
  type DocumentNode,
  type FetchResult,
} from '@apollo/client/core';
import { onError, type ErrorResponse } from '@apollo/client/link/error';
import { createClient } from 'graphql-ws';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import {
  getMainDefinition,
  Observable,
  type Observer,
} from '@apollo/client/utilities';
import { useAuthStore } from './stores/auth';
import { logErrorMessages } from '@vue/apollo-util';
import type { GraphQLError } from 'graphql';

const { wsUrl, httpUrl } = {
  wsUrl: import.meta.env.VITE_HASURA_WS_URL,
  httpUrl: import.meta.env.VITE_HASURA_URL,
};

const httpLink = (authToken: string) => {
  const header: Record<string, string> = {};
  if (authToken) {
    header.Authorization = `Bearer ${authToken}`;
  }
  return new HttpLink({
    uri: httpUrl,
    headers: header,
  });
};

const wsLink = () =>
  new GraphQLWsLink(
    createClient({
      url: wsUrl,
      async connectionParams() {
        const authStore = useAuthStore();
        try {
          const token = await authStore.restoreTokenFromCookie();
          authStore.setAuthToken(token);
          return {
            headers: {
              Authorization: token ? `Bearer ${token}` : undefined,
            },
          };
        } catch {
          authStore.setAuthToken(null);
          return {};
        }
      },
    }),
  );

const link = (authToken: string) => {
  return split(
    // split based on operation type
    ({ query }) => isSubscription(query),
    wsLink(),
    httpLink(authToken),
  );
};

const errorLink = onError((error) => {
  if (process.env.NODE_ENV !== 'production') {
    logErrorMessages(error);
  }
  networkErrorHandler(error);
  const handled = graphQlErrorHandler(error);
  if (handled) return handled;
});

const apolloLink = (authToken: string) => errorLink.concat(link(authToken));

export const apolloClient = new ApolloClient({
  link: apolloLink(''),
  cache: new InMemoryCache({
    addTypename: true,
  }),
  connectToDevTools: ['localhost'].includes(window.location.hostname),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
    },
    query: {
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
    },
  },
});

export const client = () => {
  return apolloClient;
};

export async function setApolloAuthToken(token: string) {
  apolloClient.setLink(apolloLink(token));
  if (!token) {
    await apolloClient.clearStore();
  }
}

function isSubscription(query: DocumentNode) {
  const definition = getMainDefinition(query);
  return (
    definition.kind === 'OperationDefinition' &&
    definition.operation === 'subscription'
  );
}
function graphQlErrorHandler(errorRes: ErrorResponse) {
  const { graphQLErrors, operation } = errorRes;
  if (!graphQLErrors) return;

  const invalidJwtError = graphQLErrors.find((e: GraphQLError) => {
    return e?.extensions?.code === 'invalid-jwt';
  });
  if (invalidJwtError) {
    if (isSubscription(operation.query)) {
      console.warn('Invalid JWT for subscription');
    } else {
      const observable = refetchQueryAfterTokenRefresh(errorRes);
      return observable;
    }
  }
}

function networkErrorHandler(errorRes: ErrorResponse) {
  const { networkError } = errorRes;
  if (!networkError) return;
}

function refetchQueryAfterTokenRefresh(errorRes: ErrorResponse) {
  const { operation, forward } = errorRes;
  return new Observable((observer: Observer<FetchResult>) => {
    (async () => {
      try {
        const authStore = useAuthStore();
        const token = await authStore.restoreTokenFromCookie();

        const oldHeaders = operation.getContext().headers;
        operation.setContext({
          headers: {
            ...oldHeaders,
            authorization: `Bearer ${token}`,
          },
        });
        forward(operation).subscribe(observer);
      } catch (err) {
        console.error('Token refresh failed', err);
        const authStore = useAuthStore();
        authStore.setAuthToken(null);
        observer.error?.(err);
      }
    })();
  });
}
